import jwtDecode from 'jwt-decode';

import { APIResponse } from './models';
import { AuthResponse } from './users/models/AuthResponse';

import { IS_FLIPGIVE } from 'common/env';

export const AUTH_COUNTRY_ERROR_MESSAGE = 'This app is not available for your country.';

type AuthCountryClaim = {
    sws_org_country?: unknown;
};

export type AuthCountryValidationResult =
    | {
          isValid: true;
          country: string;
          expectedCountry: string;
      }
    | {
          isValid: false;
          reason: 'invalid-token' | 'missing-country' | 'wrong-country';
          country?: string;
          expectedCountry: string;
      };

export class AuthCountryValidationError extends Error {
    readonly validation: AuthCountryValidationResult;

    constructor(validation: AuthCountryValidationResult) {
        super(AUTH_COUNTRY_ERROR_MESSAGE);
        this.name = 'AuthCountryValidationError';
        this.validation = validation;
    }
}

export const getAllowedAuthCountry = () => (IS_FLIPGIVE ? 'CA' : 'US');

export const validateAuthCountryFromToken = (
    token: string | null | undefined
): AuthCountryValidationResult => {
    const expectedCountry = getAllowedAuthCountry();

    if (!token) {
        return { isValid: false, reason: 'invalid-token', expectedCountry };
    }

    try {
        const decoded = jwtDecode<AuthCountryClaim>(token);
        const country = decoded.sws_org_country;

        if (typeof country !== 'string' || !country) {
            return { isValid: false, reason: 'missing-country', expectedCountry };
        }

        const normalizedCountry = country.toUpperCase();
        if (normalizedCountry !== expectedCountry) {
            return {
                isValid: false,
                reason: 'wrong-country',
                country: normalizedCountry,
                expectedCountry,
            };
        }

        return {
            isValid: true,
            country: normalizedCountry,
            expectedCountry,
        };
    } catch {
        return { isValid: false, reason: 'invalid-token', expectedCountry };
    }
};

export const validateAuthCountryFromAuthResponse = (
    authResponse: Pick<AuthResponse, 'idToken' | 'token'>
) => validateAuthCountryFromToken(authResponse.idToken || authResponse.token);

export const createAuthCountryValidationErrorResponse = <T, E = null>(
    validation: AuthCountryValidationResult,
    response?: Response
): APIResponse<T, E> => ({
    error: {
        response: response || null,
        error: new AuthCountryValidationError(validation),
    },
    response,
});

export const isAuthCountryValidationError = (
    error: Error | null | undefined
): error is AuthCountryValidationError => error?.name === 'AuthCountryValidationError';
